'use client';

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

const OWNER_EMAIL = 'anrandomminecrafter@gmail.com';

export function useCredits(userId: string | null) {
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  const fetchBalance = useCallback(async () => {
    if (!userId) {
      setBalance(null);
      setIsOwner(false);
      return;
    }
    setLoading(true);
    try {
      const supabase = createClient();

      // Check if this user is the owner
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role, email')
        .eq('id', userId)
        .maybeSingle();

      const ownerByRole = profile?.role === 'owner';
      const ownerByEmail = profile?.email === OWNER_EMAIL;

      if (ownerByRole || ownerByEmail) {
        setIsOwner(true);
        setBalance(Infinity);
        setLoading(false);
        return;
      }

      setIsOwner(false);

      const { data } = await supabase
        .from('user_credits')
        .select('balance')
        .eq('user_id', userId)
        .maybeSingle();

      if (data) {
        setBalance(data.balance ?? 0);
      } else {
        // Row doesn't exist yet — create it with 0 balance
        const { data: inserted } = await supabase
          .from('user_credits')
          .upsert({ user_id: userId, balance: 0 }, { onConflict: 'user_id' })
          .select('balance')
          .maybeSingle();
        setBalance(inserted?.balance ?? 0);
      }
    } catch {
      setBalance(0);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const deduct = useCallback(
    async (amount: number, description = 'AI generation'): Promise<boolean> => {
      if (!userId) return false;

      // Owner never gets deducted
      if (isOwner) return true;

      const supabase = createClient();

      // Fetch current balance
      const { data: current } = await supabase
        .from('user_credits')
        .select('balance')
        .eq('user_id', userId)
        .maybeSingle();

      const currentBalance = current?.balance ?? 0;
      if (currentBalance < amount) return false;

      const newBalance = currentBalance - amount;

      // Use upsert to handle missing rows gracefully
      const { error } = await supabase
        .from('user_credits')
        .upsert({ user_id: userId, balance: newBalance }, { onConflict: 'user_id' });

      if (!error) {
        setBalance(newBalance);
        // Log transaction (best-effort)
        supabase
          .from('credit_transactions')
          .insert({
            user_id: userId,
            amount: -amount,
            type: 'deduction',
            description,
          })
          .then(() => {})
          .catch(() => {});
        return true;
      }
      return false;
    },
    [userId, isOwner]
  );

  const refresh = fetchBalance;

  return { balance, loading, deduct, refresh, isOwner };
}
